import { useEffect, useState } from 'react';

import { getI18n } from 'react-i18next';
import { fetchApi } from 'utils/fetchApi';

import { useUserOrganization } from '~/features/organizations/hooks/useUserOrganization';


// 👇 The Look ID for the AiSatisfaction
const LOOKER_AI_SATISFACTION_BY_LANGUAGE = {
  EN: 329,
  FR: 331,
  DE: 330,
  IT: 332,
  ES: 333,
  PT: 334,
};
// 👇 The default Look ID for the AiSatisfaction
let LOOKER_AI_SATISFACTION_REPORT_ID = LOOKER_AI_SATISFACTION_BY_LANGUAGE['EN'];


export type AiSatisfactionState = {
  looker: {
    loading: boolean;
    url: string | null;
    error?: string;
  };
};
const initialState: AiSatisfactionState = {
  looker: {
    loading: false,
    url: null,
  },
};

/**
 * Handle business logic related to AiSatisfaction
 *
 * @returns AiSatisfactionState
 */
export function useAiSatisfaction() {
  const { language } = getI18n();
  const [looker, setLooker] = useState<AiSatisfactionState['looker']>(initialState.looker);
  const LANG_KEY = language.split('_')[0].toUpperCase();
  const { organizationData, isLoading, error } = useUserOrganization();

  if (LOOKER_AI_SATISFACTION_BY_LANGUAGE[LANG_KEY as keyof typeof LOOKER_AI_SATISFACTION_BY_LANGUAGE]) {
    LOOKER_AI_SATISFACTION_REPORT_ID = LOOKER_AI_SATISFACTION_BY_LANGUAGE[LANG_KEY as keyof typeof LOOKER_AI_SATISFACTION_BY_LANGUAGE];
  }

  useEffect(() => {
    let stale = false;
    async function fetchLookerURL(language: string) {
      setLooker({
        loading: true,
        url: null,
      });
      try {
        const result = await fetchApi(
          `/api/lookerDashboardUrlById?lang=${language}&id=${LOOKER_AI_SATISFACTION_REPORT_ID}`,
          {
            method: 'GET',
          },
        );
        const data = (await result.json()) as { url: string };
        if (!stale) {
          setLooker({
            loading: false,
            url: data.url,
          });
        }
      } catch (error) {
        console.error(`Failed to fetch Looker URL: ${error}`);
        if (!stale) {
          setLooker({
            loading: false,
            url: null,
            error: String(error),
          });
        }
      }
    }
    fetchLookerURL(language);

    return () => {
      stale = true;
    };
  }, [language]);

  return { org: organizationData, looker, isLoading, error };
}
